import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DATE_FORMATS } from '@callmart/types';
import type { CustomCellEditorProps } from 'ag-grid-react';
import dayjs from '@utils/dayjs';

/** The popup is sized by its content (`.ag-popup-editor` is position:absolute with no width), and the date
 *  columns themselves are 78px — narrower than the field. 168px is a small MUI outlined field holding
 *  "15.09.2026" plus the calendar adornment, i.e. the width at which nothing in the editor is clipped.
 *  Deliberately NOT in App.css beside the other editor rules: those exist because the element they size is
 *  ag-grid's own and can only be reached by a selector. This one is ours. */
const EDITOR_WIDTH = 168;

/** The date cell editor of the scenario row grids (ScenarioRowGrid, `kind: 'date'`).
 *
 *  ag-grid's stock `agDateStringCellEditor` is a native `input[type=date]`, which renders in the BROWSER/OS
 *  locale rather than the app's: on an en-US machine the open editor read "09/15/2026" while the cell beside
 *  it read "15.09.2026" through formatBudgetDate — two formats for one value in one row, on a German product,
 *  and no prop or stylesheet changes it. It also took no typed text, so the calendar widget was the only way
 *  in. ScenarioMetaForm made the same swap for the period fields (see the comment there); the adapter locale
 *  is pinned to 'de' for the same reason the budget grids pin their number formatters — the format must not
 *  depend on the machine the page happens to run on.
 *
 *  Value in and out is the column's 'YYYY-MM-DD' DATEONLY string, unchanged: on a cellDataType 'dateString'
 *  column ag-grid's checkType rejects anything else outright, and a rejected value never fires
 *  cellValueChanged. An empty or unparseable field therefore reports null (which checkType passes) and NOT
 *  '' (which it does not) — onCellValueChanged maps the null back to the '' the draft holds.
 *
 *  Reporting is by the `onValueChange` PROP, not by a getValue() on a ref: `reactiveCustomComponents`
 *  defaults to true in ag-grid 32, and CellEditorComponentProxy then reads only isCancelBeforeStart,
 *  isCancelAfterEnd, focusIn, focusOut and afterGuiAttached off the ref — a getValue() there is silently
 *  ignored (which is what makes the unused NumericEditor in components/AgGrid dead as well as unreferenced).
 *  Every keystroke reports upward, so the grid already holds the value when the editor is closed by a click
 *  elsewhere: with stopEditingWhenCellsLoseFocus the popup is modal, and its closedCallback COMMITS
 *  (onPopupEditorClosed → stopEditingAndFocus). Nothing has to be flushed on blur. */
const ScenarioDateCellEditor = ({ value, onValueChange }: CustomCellEditorProps<unknown, string>) => (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de" dateLibInstance={dayjs}>
        <DatePicker
            format={DATE_FORMATS.DATE}
            value={value ? dayjs(value, DATE_FORMATS.ISO_DATE) : null}
            onChange={(next) => onValueChange(next?.isValid() ? next.format(DATE_FORMATS.ISO_DATE) : null)}
            slotProps={{
                // Focused on open, like any cell editor: the cell was entered to be typed in.
                textField: { size: 'small', autoFocus: true, inputProps: { className: 'notranslate' } },
                // The calendar is portalled to document.body, i.e. OUTSIDE the ag-grid popup this editor
                // lives in — and a popup editor on a grid with stopEditingWhenCellsLoseFocus is added
                // `modal: true`, so a document-level mousedown anywhere outside it closes it. Clicking a day
                // would close the editor instead of picking that day. `ag-custom-component-popup` is
                // ag-grid's own escape hatch for exactly this: popupService walks up parentElement from the
                // clicked node (isElementWithinCustomPopup) and leaves the editor open when it finds the
                // class. The same walk is what keeps stopEditingWhenCellsLoseFocus off the calendar.
                popper: { className: 'ag-custom-component-popup' },
            }}
            // `.ag-popup-editor` sets `user-select: none` over the whole popup, which takes double-click and
            // drag selection away from the one control here whose point is typing.
            sx={{ width: EDITOR_WIDTH, userSelect: 'text' }}
        />
    </LocalizationProvider>
);

export default ScenarioDateCellEditor;
